import React, { useEffect, useReducer, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FakeText } from '@totalsoft/rocket-ui';
import { initialState, reducer } from '../../reducers/containerReducer';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { SCORING_CATEGORY_QUERY, INPUT_TYPES_QUERY, SCALES_QUERY } from './queries';
import { ADD_UPDATE_SCORING_CATEGORY, ADD_UPDATE_SCORING_CATEGORY_DETAIL, DELETE_SCORING_CATEGORY_DETAIL } from './mutations';
import { SCORING_CATEGORY_UPDATED, SCORING_CATEGORY_DETAIL_UPDATED, SCORING_CATEGORY_DETAIL_DELETED } from './subscriptions';
import ScoringCategoryGeneralDataComponent from './ScoringCategoryGeneralDataComponent';
import ScoringCategoryDetailListCard from './ScoringCategoryDetailListCard';
import { useValidation } from '@totalsoft/pure-validations-react';
import { scoringCategoryValidator } from 'features/scoringCategory/validators';
import { scoringCategoryDetailValidator } from 'features/scoringCategory/validators';
import { isValid } from '@totalsoft/pure-validations-react';

const ScoringCategoryContainer = ({ scoringId, onChangeTitle, onRedirect, onSaveFnChanged }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [scoringCategoryValidation, validateScoringCategory] = useValidation(scoringCategoryValidator);
  const validatorScoringCategoryDetail = useMemo(
    () => scoringCategoryDetailValidator(localState.header.inputTypeId),
    [localState.header.inputTypeId]
  );
  const [scoringCategoryDetailsValidation, validateScoringCategoryDetails] = useValidation(validatorScoringCategoryDetail);
  const minMaxInvalid = useMemo(() => {
    return !localState.details.some(
      (_, idx) =>
        isValid(scoringCategoryDetailsValidation[idx].minValue) === false ||
        isValid(scoringCategoryDetailsValidation[idx].maxValue) === false
    );
  }, [localState.details, scoringCategoryDetailsValidation]);
  const canSaveGeneralData = useMemo(() => {
    return (
      (localState.header.changed || localState.details.some((item, _) => item.changed)) &&
      isValid(scoringCategoryValidation) &&
      !localState.details.some((_, idx) => isValid(scoringCategoryDetailsValidation[idx]) === false)
    );
  }, [localState.details, localState.header.changed, scoringCategoryDetailsValidation, scoringCategoryValidation]);

  const { data, loading: loadingHeader } = useQuery(SCORING_CATEGORY_QUERY, {
    variables: { scoringId },
    skip: scoringId === undefined
  });

  const { data: inputTypesData, loading: inputTypesLoading } = useQuery(INPUT_TYPES_QUERY);

  const { data: scalesData, loading: scalesLoading } = useQuery(SCALES_QUERY);

  const [addOrUpdateScoringCategory] = useMutation(ADD_UPDATE_SCORING_CATEGORY);

  const [addOrUpdateScoringCategoryDetail] = useMutation(ADD_UPDATE_SCORING_CATEGORY_DETAIL);

  const [deleteScoringCategoryDetail] = useMutation(DELETE_SCORING_CATEGORY_DETAIL);

  useSubscription(SCORING_CATEGORY_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ScoringCategories.ScoringCategoryUpdated'), 'success');
      if (scoringId === undefined) onRedirect(data.scoringCategoryUpdated.scoringCategory.scoringId);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedScoringCategories' });
      client.cache.evict({
        id: `ScoringCategoryItem:{"scoringId":${data.scoringCategoryUpdated.scoringCategory.scoringId}}`
      });
      client.cache.gc();
    }
  });

  useSubscription(SCORING_CATEGORY_DETAIL_UPDATED, {
    onData: ({ data: { data }, client }) => {
      client.cache.evict({
        id: `ScoringCategoryItem:{"scoringId":${data.scoringCategoryDetailUpdated.scoringDetail.scoringId}}`
      });
      client.cache.evict({
        id: `ScoringCategoryDetailItem:{"scoringDetailId":${data.scoringCategoryDetailUpdated.scoringDetail.scoringDetailId}}`
      });
      client.cache.gc();
    }
  });

  useSubscription(SCORING_CATEGORY_DETAIL_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ScoringCategories.ScoringCategoryDetailDeleted'), 'success');
      client.cache.evict({
        id: `ScoringCategoryDetailItem:{"scoringDetailId":${data.scoringCategoryDetailDeleted.scoringDetailId}}`
      });
      client.cache.gc();
    }
  });

  useEffect(() => {
    if (localState.header.scoringName) {
      onChangeTitle(localState.header.scoringName);
    }
  }, [localState.header.scoringName, onChangeTitle]);

  useEffect(() => {
    if (!scoringId) dispatch({ type: 'OnScoringCategoryAdd' });
    else if (data && data.scoringCategory) dispatch({ type: 'OnInitState', scoringCategory: data.scoringCategory });
  }, [data, scoringId]);

  useEffect(() => {
    validateScoringCategory(localState.header);
  }, [localState.header, validateScoringCategory]);

  useEffect(() => {
    validateScoringCategoryDetails(localState.details);
  }, [localState.details, validateScoringCategoryDetails]);

  const onSave = useCallback(() => {
    // eslint-disable-next-line no-unused-vars
    const { changed, ...scoringCategory } = localState.header;
    dispatch({ type: 'OnHeaderSave' });
    addOrUpdateScoringCategory({
      variables: { defaultDetailId: localState.defaultDetail.scoringDetailId, input: scoringCategory }
    });

    localState.details.forEach((detail, index) => {
      dispatch({ type: 'OnDetailSave', index });
      // eslint-disable-next-line no-unused-vars
      const { changed, isExpanded, ...scoringCategoryDetail } = detail;
      addOrUpdateScoringCategoryDetail({
        variables: { input: scoringCategoryDetail }
      });
    });
  }, [
    addOrUpdateScoringCategory,
    addOrUpdateScoringCategoryDetail,
    localState.defaultDetail.scoringDetailId,
    localState.details,
    localState.header
  ]);

  const onDelete = useCallback(
    (data, index) => {
      dispatch({ type: 'OnDetailDelete', index });
      deleteScoringCategoryDetail({
        variables: { input: { scoringId: localState.header.scoringId, scoringDetailId: data } }
      });
    },
    [deleteScoringCategoryDetail, localState.header.scoringId]
  );

  useEffect(() => {
    onSaveFnChanged(onSave, !canSaveGeneralData);
  }, [onSaveFnChanged, onSave, canSaveGeneralData]);

  const loading = loadingHeader || inputTypesLoading || scalesLoading || localState === initialState;

  return loading ? (
    <FakeText lines={10} />
  ) : (
    <>
      <ScoringCategoryGeneralDataComponent
        scoringCategory={localState.header}
        defaultDetail={localState.defaultDetail}
        defaultLookup={data && data.scoringCategory ? data.scoringCategory.scoringCategoryDetails : []}
        inputTypes={inputTypesData ? inputTypesData.inputTypes : []}
        scales={scalesData ? scalesData.scales : []}
        dispatch={dispatch}
        visibleNumeric={localState.header.inputTypeId}
        validation={scoringCategoryValidation}
      />
      {scoringId !== null && scoringId !== undefined && (
        <ScoringCategoryDetailListCard
          scoringCategoryDetails={localState.details}
          dispatch={dispatch}
          scaleFormat={parseInt(localState.header.scale) || 0}
          onDelete={onDelete}
          visibleNumeric={localState.header.inputTypeId}
          validation={scoringCategoryDetailsValidation}
          minMaxInvalid={minMaxInvalid}
        />
      )}
    </>
  );
};

ScoringCategoryContainer.propTypes = {
  scoringId: PropTypes.number,
  onChangeTitle: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired
};

export default ScoringCategoryContainer;
